import JumboCardQuick from "@jumbo/components/JumboCardQuick";
import { useJumboTheme } from "@jumbo/hooks";
import Div from "@jumbo/shared/Div";
import {
  Button,
  Typography,
  Breadcrumbs,
  Snackbar,
  Alert,
  AlertTitle,
  TextField,
  Box,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import mapValues from "lodash/mapValues";

import { Form, Formik } from "formik";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";

import BrowserUpdatedIcon from "@mui/icons-material/BrowserUpdated";
import {
  editDSOKDetails,
  generateToken,
  getCustomer,
  getCustomers,
} from "app/services/api/management";

import * as Yup from "yup";
import DevicesField, {
  getDevicesFieldValidationSchema,
} from "./fields/DevicesField";
import LocationsField, {
  getLocationsFieldValidationSchema,
} from "./fields/LocationsField";
import SettingsField, {
  getSettingsFieldValidationSchema,
} from "./fields/SettingsField";
import MaintenanceField, {
  getMaintenanceFieldValidationSchema,
} from "./fields/MaintenanceField";

import UISettingsField, {
  UI_SETTINGS_FIELD_SCHEMA,
  getUISettingsFieldValidationSchema,
} from "./fields/UISettingsField";
import TokenIcon from "@mui/icons-material/Token";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { DSOK_DEVICE_SERVER_PORT_START } from "app/utils/constants/settings";

import SmartkioskCustomerFields from "./smartkioskFields/SmartkioskCustomerFields";
import SmartkioskSettingsFields from "./smartkioskFields/SmartkioskSettingsFields";
import SmartkioskColors from "./smartkioskFields/SmartkioskColors";
import SmartkioskProducts from "./smartkioskFields/SmartkioskProducts";
import SmartkioskCart from "./smartkioskFields/SmartkioskCart";
import SmartkioskCategories from "./smartkioskFields/SmartkioskCategories";
import SmartkioskDynamicComponents from "./smartkioskFields/SmartkioskDynamicComponents";
import SmartkioskTranslations from "./smartkioskFields/SmartkioskTranslations";
import PrinterSettingsFields, {
  getPrinterSettingsFieldSchema,
} from "./fields/PrinterSettingsFields";
import SmartkioskIntegrationFields, {
  INTEGRATIONS_FIELD_SCHEMA,
  ORDER_MANAGEMENT_FIELD_SCHEMA,
  PRODUCTS_MANAGEMENT_FIELD_SCHEMA,
} from "./smartkioskFields/SmartkioskIntegrationFields";
import SmartkioskLocationsField from "./smartkioskFields/SmartkioskLocationFields";
import SmartkioskUISettingsFields from "./smartkioskFields/SmartkioskUISettingsFields";
import DsokCustomerField, {
  DSOK_CUSTOMER_FIELD_SCHEMA,
  getDsokCustomerFieldValidationSchema,
} from "./fields/DsokCustomerField";
import { getSmartkioskInitialValues } from "./utils/getSmartkioskInitialValues";
import { sanitizeLogoUrlString } from "./utils/sanitizeLogoUrlString";

const SmartkioskDetails = () => {
  const { url } = useParams();
  const { t } = useTranslation();
  const { theme } = useJumboTheme();
  const { getAuthUser } = useJumboAuth();
  const authData = getAuthUser() || {};
  const { customer: authUser, customerId: authCustomerId } = authData;
  const [customer, setCustomer] = useState();
  const [unsaved, setUnsaved] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const formRef = useRef();
  const [expanded, setExpanded] = useState(false);
  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };
  const customerUrl = url || authUser?.url || "";
  const isAdmin = authUser?.customerType === 1;
  const isPartner = authUser?.customerType === 2;
  const { showDialog, hideDialog } = useJumboDialog();

  const validationSchema = Yup.object().shape({
    customer: Yup.object().shape({
      ...getDsokCustomerFieldValidationSchema(t),
      license: Yup.object()
        .shape({
          type: Yup.string()
            .oneOf(["basic", "plus", "max"])
            .required(t("messages.checkRequiredFields")),
        })
        .required(),
      customerRelation: Yup.object()
        .shape({
          customerType: Yup.string()
            .oneOf(["standalone", "parent", "child"])
            .required(t("messages.checkRequiredFields")),
          parentCustomerId: Yup.string()
            .nullable()
            .when("customerType", {
              is: "child",
              then: (schema) =>
                schema
                  .required(
                    t(
                      "pages.dsokDetails.customerSettingsTabs.relations.validation.parentRequired",
                    ),
                  )
                  .test(
                    "not-self-parent",
                    t(
                      "pages.dsokDetails.customerSettingsTabs.relations.validation.selfParent",
                    ),
                    (value) => !value || String(value) !== String(customerUrl),
                  ),
              otherwise: (schema) => schema.nullable(),
            }),
          relationshipType: Yup.string()
            .nullable()
            .when("customerType", {
              is: (v) => v === "parent" || v === "child",
              then: (schema) =>
                schema.oneOf(["multiLicense", "multiKiosk", null]).nullable(),
              otherwise: (schema) => schema.oneOf([null]).nullable(),
            }),
        })
        .required(),
      locations: Yup.lazy((lazyObj) =>
        Yup.object(
          mapValues(lazyObj, () =>
            Yup.object(getLocationsFieldValidationSchema(t)),
          ),
        ),
      ),
      devices: Yup.lazy((lazyObj) =>
        Yup.object(
          mapValues(lazyObj, () =>
            Yup.object(getDevicesFieldValidationSchema(t)),
          ),
        ),
      ),
    }),
    settings: Yup.object().shape(getSettingsFieldValidationSchema(t)),
    // maintenance: Yup.object().shape(getMaintenanceFieldValidationSchema(t)),
    // printerSettings: Yup.object().shape(
    //   getPrinterSettingsFieldValidationSchema(t)
    // ),
    // orderManagement: Yup.object().shape(
    //   getOrderManagementFieldValidationSchema(t)
    // ),
    // uiSettings: Yup.object().shape(getUISettingsFieldValidationSchema(t)),
  });

  const loadCustomer = async () => {
    setLoading(true);
    const customerData = await getCustomer(customerUrl);
    setCustomer(customerData);
    setLoading(false);
  };

  const transformObject = (input) => {
    return Object.values(input);
  };

  const handleUpdateCustomer = async (settings) => {
    if (settings?.customer) {
      if (typeof settings.customer.mainLogo === "string") {
        settings.customer.mainLogo = sanitizeLogoUrlString(
          settings.customer.mainLogo,
        );
      }
      if (typeof settings.customer.tableImage === "string") {
        settings.customer.tableImage = sanitizeLogoUrlString(
          settings.customer.tableImage,
        );
      }
    }
    const prevRelation =
      customer?.dsokSettings?.customer?.customerRelation ??
      customer?.dsokDetails?.customer?.customerRelation ??
      null;
    const prevRelationshipType = prevRelation?.relationshipType ?? null;
    const prevCustomerType = prevRelation?.customerType ?? null;

    Object.keys(settings?.customer.locations || {}).forEach(
      (key) => (settings.customer.locations[key].id = +key),
    );
    Object.keys(settings?.customer?.devices || {}).forEach((key) => {
      settings.customer.devices[key].id = +key;
      const posPort = DSOK_DEVICE_SERVER_PORT_START + (+key - 1) * 16;
      const ekasaPort = posPort + 1;
      const generalServerPort = ekasaPort + 1;
      // console.log('device', key, posPort, ekasaPort, generalServerPort)
      settings.customer.devices[key].pos.serverPort = posPort;
      settings.customer.devices[key].ekasa.serverPort = ekasaPort;
      settings.customer.devices[key].generalServer.serverPort =
        generalServerPort;
    });
    // update categoriesDisplaySettings
    settings.settings.smartkiosk.categoriesDisplaySettings = transformObject(
      settings.settings.smartkiosk.categoriesDisplaySettings,
    );
    settings.settings.smartkiosk.dynamicComponents = transformObject(
      settings.settings.smartkiosk.dynamicComponents,
    );

    // Ensure color fields are persisted under settings.colors as expected by backend
    settings.settings = settings.settings || {};
    settings.settings.colors = {
      ...(settings.settings.colors || {}),
      ...(settings.colors || {}),
    };
    // Optional: avoid sending duplicate top-level colors
    if (settings.colors) {
      delete settings.colors;
    }
    const dsok = { ...customer.dsokDetails, ...settings };
    setUpdating(true);
    try {
      await editDSOKDetails({ customerUrl, ...dsok });
      await loadCustomer();

      // If this customer is a parent and relationshipType changed,
      // propagate the new value to all children linked to this parent.
      const nextRelation = settings?.customer?.customerRelation ?? null;
      const nextCustomerType = nextRelation?.customerType ?? null;
      const nextRelationshipType = nextRelation?.relationshipType ?? null;
      const relationshipTypeChanged =
        prevCustomerType === "parent" &&
        nextCustomerType === "parent" &&
        prevRelationshipType !== nextRelationshipType &&
        (nextRelationshipType === "multiLicense" ||
          nextRelationshipType === "multiKiosk");

      if (relationshipTypeChanged && authCustomerId) {
        try {
          const result = await getCustomers(authCustomerId);
          const list = Array.isArray(result) ? result : [];
          const customersData = list.map((c) => ({
            ...c.customerData,
            users: c.users,
          }));

          const parentKeyCandidates = [
            customerUrl,
            String(customer?.url ?? ""),
            String(customer?.customerId ?? ""),
            String(customer?.id ?? ""),
          ].filter((v) => v && v !== "undefined" && v !== "null");

          const parseDsokSettings = (dsokSettings) => {
            if (!dsokSettings) return null;
            if (typeof dsokSettings === "string") {
              try {
                return JSON.parse(dsokSettings);
              } catch (_e) {
                return null;
              }
            }
            return dsokSettings;
          };

          const children = customersData.filter((c) => {
            const dsokSettings = parseDsokSettings(c.dsokSettings);
            const rel =
              dsokSettings?.customer?.customerRelation ?? c?.customerRelation;
            if (!rel) return false;
            if (rel.customerType !== "child") return false;
            const parentId = rel.parentCustomerId;
            if (!parentId) return false;
            return parentKeyCandidates.includes(String(parentId));
          });

          for (const child of children) {
            const childUrl =
              child?.url ?? child?.customerUrl ?? child?.customer ?? null;
            if (!childUrl) continue;

            const childCustomer = await getCustomer(String(childUrl));
            if (!childCustomer?.dsokDetails) continue;

            const childSettings = getSmartkioskInitialValues(childCustomer);
            childSettings.customer = childSettings.customer || {};
            childSettings.customer.customerRelation =
              childSettings.customer.customerRelation || {};
            childSettings.customer.customerRelation.customerType = "child";
            childSettings.customer.customerRelation.parentCustomerId =
              childSettings.customer.customerRelation.parentCustomerId ??
              customerUrl;
            childSettings.customer.customerRelation.relationshipType =
              nextRelationshipType;

            // Reuse the same persistence path as normal updates.
            // (We intentionally don't show a modal per child; failures are best-effort.)
            try {
              // mimic the same normalization as handleUpdateCustomer for device ports, etc.
              Object.keys(childSettings?.customer?.locations || {}).forEach(
                (key) => (childSettings.customer.locations[key].id = +key),
              );
              Object.keys(childSettings?.customer?.devices || {}).forEach(
                (key) => {
                  childSettings.customer.devices[key].id = +key;
                  const posPort =
                    DSOK_DEVICE_SERVER_PORT_START + (+key - 1) * 16;
                  const ekasaPort = posPort + 1;
                  const generalServerPort = ekasaPort + 1;
                  childSettings.customer.devices[key].pos.serverPort = posPort;
                  childSettings.customer.devices[key].ekasa.serverPort =
                    ekasaPort;
                  childSettings.customer.devices[key].generalServer.serverPort =
                    generalServerPort;
                },
              );
              childSettings.settings = childSettings.settings || {};
              childSettings.settings.smartkiosk =
                childSettings.settings.smartkiosk || {};
              if (
                childSettings.settings.smartkiosk.categoriesDisplaySettings &&
                !Array.isArray(
                  childSettings.settings.smartkiosk.categoriesDisplaySettings,
                )
              ) {
                childSettings.settings.smartkiosk.categoriesDisplaySettings =
                  Object.values(
                    childSettings.settings.smartkiosk.categoriesDisplaySettings,
                  );
              }
              if (
                childSettings.settings.smartkiosk.dynamicComponents &&
                !Array.isArray(
                  childSettings.settings.smartkiosk.dynamicComponents,
                )
              ) {
                childSettings.settings.smartkiosk.dynamicComponents =
                  Object.values(
                    childSettings.settings.smartkiosk.dynamicComponents,
                  );
              }

              const childDsok = {
                ...childCustomer.dsokDetails,
                ...childSettings,
              };
              await editDSOKDetails({
                customerUrl: String(childUrl),
                ...childDsok,
              });
            } catch (_e) {
              // best-effort propagation
            }
          }
        } catch (_e) {
          // best-effort propagation; ignore
        }
      }

      const message = `${t("buttons.update")} ${t(
        "notifications.wasSuccessful",
      )}`;
      enqueueSnackbar(message, {
        variant: "success",
      });
    } catch (error) {
      const message = `${t("buttons.update")} ${t("notifications.wasFailed")}`;
      enqueueSnackbar(message, {
        variant: "error",
      });
      setUpdating(false);
    }
    setUpdating(false);
  };

  const handleGenerateToken = async () => {
    setUpdating(true);
    try {
      const token = await generateToken(customer?.id);
      showDialog({
        variant: "default",
        title: t("pages.dsokDetails.generateToken"),
        content: (
          <Box
            display="flex"
            flexDirection="column"
            gap={1}
            alignItems="flex-end"
          >
            <TextField
              fullWidth
              name="token"
              value={token}
              multiline
              rows={3}
              margin="dense"
              sx={{ mt: 0 }}
              InputProps={{
                readOnly: true,
              }}
            />
            <Button
              variant="contained"
              size="small"
              sx={{
                boxShadow: "none",
                height: "38px",
                width: "fit-content",
              }}
              startIcon={<ContentCopyIcon />}
              onClick={() => {
                navigator.clipboard.writeText(token);
                const message = `${t("buttons.copy")} ${t(
                  "notifications.wasSuccessful",
                )}`;
                enqueueSnackbar(message, {
                  variant: "success",
                });
                hideDialog();
              }}
            >
              {t("buttons.copy")}
            </Button>
          </Box>
        ),
      });
    } catch (error) {
      const message = `${t("pages.dsokDetails.generateToken")} ${t(
        "notifications.wasFailed",
      )}`;
      enqueueSnackbar(message, {
        variant: "error",
      });
      setUpdating(false);
    }
    setUpdating(false);
  };

  const getInitialValues = () => getSmartkioskInitialValues(customer);

  useEffect(() => {
    loadCustomer();
  }, []);

  const initialValues = customer && getInitialValues();
  const dsokSettings = customer?.dsokSettings;
  const enableDSOKPrinterSettings =
    dsokSettings?.customer?.enableDSOKPrinterSettings;

  if (loading) {
    return <CircularProgress sx={{ m: "5px 50%" }} />;
  }

  return (
    <>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <Link to="/">{t("sidebar.menuItem.home")}</Link>
        {isAdmin && (
          <Link to="/customers">{t("sidebar.menuItem.customers")}</Link>
        )}
        <Typography color="text.primary">
          {`${"[" + customer?.id + "] " + customer?.companyName || ""}`}
        </Typography>
      </Breadcrumbs>
      <Div
        sx={{
          display: "flex",
        }}
      >
        <JumboCardQuick
          title={
            <Typography
              component={"div"}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                minHeight: 29,
                [theme.breakpoints.down("md")]: {
                  flexWrap: "wrap",
                },
              }}
            >
              <Typography
                variant={"h4"}
                mb={0}
                sx={{
                  minWidth: 245,
                  [theme.breakpoints.down("md")]: {
                    minWidth: "100%",
                    marginBottom: 2,
                  },
                }}
              >
                {`${customer?.companyName || ""}`}
              </Typography>
              <Div sx={{ display: "flex", gap: 2 }}>
                {isAdmin && (
                  <Button
                    variant="contained"
                    size="small"
                    sx={{
                      boxShadow: "none",
                      height: "38px",
                    }}
                    startIcon={<TokenIcon />}
                    disabled={updating}
                    onClick={handleGenerateToken}
                  >
                    {t("pages.dsokDetails.generateToken")}
                  </Button>
                )}
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    boxShadow: "none",
                    height: "38px",
                  }}
                  startIcon={<BrowserUpdatedIcon />}
                  disabled={updating}
                  onClick={() => {
                    if (formRef.current) {
                      if (!formRef.current.isValid) {
                        const message = `${t("messages.checkRequiredFields")}`;
                        enqueueSnackbar(message, {
                          variant: "error",
                        });
                        return;
                      }
                      formRef.current.handleSubmit();
                    }
                  }}
                >
                  {t("buttons.update")}
                </Button>
              </Div>
            </Typography>
          }
          headerSx={{
            borderBottom: 1,
            borderBottomColor: "divider",
            "& .MuiCardHeader-action": {
              my: -0.75,
            },
          }}
          sx={{
            flex: 1,
            mr: 2,
          }}
          wrapperSx={{
            p: 1,
            "&:last-child": {
              pb: 2,
            },
            "& .MuiCollapse-entered:last-child": {
              "& .MuiListItemButton-root": {
                borderBottom: 0,
                borderBottomColor: "transparent",
              },
            },
          }}
        >
          {customer && (
            <Formik
              innerRef={formRef}
              validateOnChange={true}
              validationSchema={validationSchema}
              initialValues={initialValues}
              onSubmit={async (data, { setSubmitting }) => {
                setSubmitting(true);
                await handleUpdateCustomer(data);
                setSubmitting(false);
              }}
            >
              {({ isSubmitting, values, setFieldValue }) => (
                <Form style={{ textAlign: "left" }} noValidate>
                  {isAdmin && (
                    <>
                      <SmartkioskCustomerFields
                        handleChange={handleChange}
                        expanded={expanded}
                      />
                      <SmartkioskUISettingsFields
                        handleChange={handleChange}
                        expanded={expanded}
                      />
                    </>
                  )}
                  {(isAdmin || isPartner) && (
                    <>
                      <SmartkioskLocationsField
                        handleChange={handleChange}
                        expanded={expanded}
                        isAdmin={isAdmin}
                        isPartner={isPartner}
                        customer={customer}
                      />
                    </>
                  )}
                  <DevicesField
                    handleChange={handleChange}
                    expanded={expanded}
                    isAdmin={isAdmin}
                    customerUrl={customerUrl}
                    isPartner={isPartner}
                  />
                  <SmartkioskSettingsFields
                    handleChange={handleChange}
                    expanded={expanded}
                    isAdmin={isAdmin}
                    isPartner={isPartner}
                  />
                  {isAdmin && (
                    <>
                      <SmartkioskColors
                        handleChange={handleChange}
                        expanded={expanded}
                      />
                      <SmartkioskProducts
                        handleChange={handleChange}
                        expanded={expanded}
                      />
                      <SmartkioskCart
                        handleChange={handleChange}
                        expanded={expanded}
                      />
                      <SmartkioskCategories
                        handleChange={handleChange}
                        expanded={expanded}
                      />
                      <SmartkioskDynamicComponents
                        handleChange={handleChange}
                        expanded={expanded}
                      />
                      <SmartkioskIntegrationFields
                        handleChange={handleChange}
                        expanded={expanded}
                      />
                      {enableDSOKPrinterSettings && (
                        <PrinterSettingsFields
                          handleChange={handleChange}
                          expanded={expanded}
                        />
                      )}
                      <SmartkioskTranslations
                        handleChange={handleChange}
                        expanded={expanded}
                      />
                    </>
                  )}
                  <MaintenanceField
                    handleChange={handleChange}
                    expanded={expanded}
                  />
                  {/* 
                  <PrinterSettingsFields
                    handleChange={handleChange}
                    expanded={expanded}
                  />

                  {isAdmin && (
                    <>
                      <OrderManagementField
                        handleChange={handleChange}
                        expanded={expanded}
                      />
                    </>
                  )}
                  {(isAdmin || isPartner) && (
                    <UISettingsField
                      handleChange={handleChange}
                      expanded={expanded}
                    />
                  )} */}
                </Form>
              )}
            </Formik>
          )}
        </JumboCardQuick>
      </Div>
      <Snackbar
        open={unsaved}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="info">
          <AlertTitle>{t("messages.unsavedChanges.title")}</AlertTitle>
          {t("messages.unsavedChanges.description")}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SmartkioskDetails;
